export interface LegalTemplateVersion {
  versionNumber: number;
  title: string;
  content: string;
  status: 'draft' | 'active' | 'archived';
  createdAt: string;
  createdBy: string;
  notes?: string;
}

export interface LegalTemplate {
  id: number;
  typeKey: string;
  typeLabel: string;
  typeIcon: string;
  currentVersion: number;
  versions: LegalTemplateVersion[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export const AVAILABLE_VARIABLES = [
  { key: '{{radio_name}}', label: 'Nombre de la Radio', example: 'Radio Estelar 104.5' },
  { key: '{{frequency_web}}', label: 'Frecuencia RFW', example: '104.5 RFW' },
  { key: '{{plan_name}}', label: 'Nombre del Plan', example: 'Pro' },
  { key: '{{monthly_price}}', label: 'Precio Mensual', example: '$19.99 USD' },
  { key: '{{annual_license_name}}', label: 'Nombre Licencia Anual', example: 'Licencia Anual de Frecuencia RFW' },
  { key: '{{annual_license_amount}}', label: 'Monto Licencia Anual', example: '$49.95 USD' },
  { key: '{{storage_limit}}', label: 'Límite de Storage', example: '50 GB' },
  { key: '{{contract_date}}', label: 'Fecha del Contrato', example: '01 de Julio de 2026' },
  { key: '{{renewal_date}}', label: 'Fecha de Renovación', example: '01 de Julio de 2027' },
  { key: '{{representative_name}}', label: 'Nombre del Representante', example: 'Carlos Mendoza' },
  { key: '{{company_name}}', label: 'Nombre de la Empresa', example: 'Estelar Media Group S.A.' },
] as const;

export type TemplateVariableKey = typeof AVAILABLE_VARIABLES[number]['key'];

export const TYPE_DEFINITIONS: Record<string, { label: string; icon: string; description: string }> = {
  terms_of_service: {
    label: 'Términos de Servicio',
    icon: 'ri-file-text-line',
    description: 'Condiciones generales de uso de la plataforma RadioFrecuenciaWeb',
  },
  privacy_policy: {
    label: 'Política de Privacidad',
    icon: 'ri-shield-check-line',
    description: 'Manejo de datos personales de radios y oyentes',
  },
  radio_contract: {
    label: 'Contrato de Radio',
    icon: 'ri-article-line',
    description: 'Contrato de prestación de servicios para emisoras',
  },
  annual_frequency_license: {
    label: 'Licencia Anual de Frecuencia RFW',
    icon: 'ri-radar-line',
    description: 'Términos de la licencia anual de reserva de frecuencia',
  },
  content_policies: {
    label: 'Políticas de Contenido',
    icon: 'ri-file-warning-line',
    description: 'Normas sobre el contenido emitido por las radios',
  },
  copyright_music: {
    label: 'Derechos de Autor y Música',
    icon: 'ri-music-2-line',
    description: 'Responsabilidades sobre derechos de autor y regalías',
  },
  suspension_policy: {
    label: 'Política de Suspensión',
    icon: 'ri-pause-circle-line',
    description: 'Causales y procedimiento de suspensión de servicio',
  },
  storage_policy: {
    label: 'Política de Storage',
    icon: 'ri-hard-drive-2-line',
    description: 'Límites y uso del almacenamiento en la plataforma',
  },
  app_policy: {
    label: 'Política de Apps',
    icon: 'ri-smartphone-line',
    description: 'Uso de las aplicaciones móviles oficiales de RFW',
  },
  rebroadcast_policy: {
    label: 'Política de Retransmisión a Redes',
    icon: 'ri-share-forward-line',
    description: 'Reglas para retransmisión en redes sociales y plataformas externas',
  },
};

export const legalTemplates: LegalTemplate[] = [
  {
    id: 1,
    typeKey: 'terms_of_service',
    typeLabel: 'Términos de Servicio',
    typeIcon: 'ri-file-text-line',
    currentVersion: 3,
    isActive: true,
    createdAt: '2025-01-15T10:00:00',
    updatedAt: '2026-06-01T14:30:00',
    versions: [
      {
        versionNumber: 1,
        title: 'Términos de Servicio — Versión Original',
        status: 'archived',
        content: `TÉRMINOS DE SERVICIO DE RADIOFRECUENCIAWEB

1. ACEPTACIÓN DE LOS TÉRMINOS

Al acceder y utilizar la plataforma RadioFrecuenciaWeb (en adelante, "RFW" o "la Plataforma"), usted acepta cumplir con los presentes Términos de Servicio. Si no está de acuerdo con estos términos, no utilice la Plataforma.

2. DESCRIPCIÓN DEL SERVICIO

RFW es una plataforma de administración, gestión, promoción e interacción para emisoras de radio online. La Plataforma permite a las emisoras:

- Crear un perfil público con su identidad visual
- Gestionar su programación y contenidos
- Interactuar con su audiencia a través de la cabina de transmisión
- Publicar fotos, videos y eventos
- Ser descubierta por nuevos oyentes a través del directorio

La radio {{radio_name}}, operando en la frecuencia {{frequency_web}} bajo el plan {{plan_name}}, acepta estos términos en su totalidad.

3. ELEGIBILIDAD

Para utilizar RFW como emisora, usted debe:
- Ser mayor de 18 años o contar con autorización legal
- Ser el propietario o representante autorizado de la radio
- Proporcionar información veraz y actualizada
- Cumplir con todas las leyes aplicables en su jurisdicción`,
        createdAt: '2025-01-15T10:00:00',
        createdBy: 'Carlos Mendoza',
        notes: 'Versión inicial de los términos.',
      },
      {
        versionNumber: 2,
        title: 'Términos de Servicio — Actualización 2025',
        status: 'archived',
        content: `TÉRMINOS DE SERVICIO DE RADIOFRECUENCIAWEB

1. ACEPTACIÓN DE LOS TÉRMINOS

Al acceder y utilizar la plataforma RadioFrecuenciaWeb (en adelante, "RFW" o "la Plataforma"), usted acepta cumplir con los presentes Términos de Servicio. Si no está de acuerdo con estos términos, no utilice la Plataforma.

2. DESCRIPCIÓN DEL SERVICIO

RFW es una plataforma de administración, gestión, promoción e interacción para emisoras de radio online. La Plataforma permite a las emisoras gestionar su presencia digital y conectar con oyentes.

3. CUENTAS DE USUARIO

Usted es responsable de mantener la confidencialidad de sus credenciales de acceso. RFW no se hace responsable por el uso no autorizado de su cuenta.

La radio {{radio_name}}, representada por {{representative_name}}, acepta estos términos.

4. MODIFICACIONES

RFW se reserva el derecho de modificar estos términos en cualquier momento. Los cambios serán notificados con 30 días de anticipación.`,
        createdAt: '2025-08-20T09:00:00',
        createdBy: 'María Gutiérrez',
        notes: 'Agregada cláusula de cuentas de usuario y modificaciones.',
      },
      {
        versionNumber: 3,
        title: 'Términos de Servicio — Vigente 2026',
        status: 'active',
        content: `TÉRMINOS DE SERVICIO DE RADIOFRECUENCIAWEB

1. ACEPTACIÓN DE LOS TÉRMINOS

Al acceder y utilizar la plataforma RadioFrecuenciaWeb (en adelante, "RFW" o "la Plataforma"), {{radio_name}} y su representante {{representative_name}} aceptan cumplir con los presentes Términos de Servicio.

2. DESCRIPCIÓN DEL SERVICIO

RFW es una plataforma de administración, gestión, promoción e interacción para emisoras de radio online. La Plataforma permite:
- Crear y gestionar un perfil público de emisora
- Administrar programación y contenidos
- Interactuar con la audiencia a través de la cabina de transmisión
- Publicar contenido multimedia
- Ser descubierta en el directorio público

3. CUENTAS Y SEGURIDAD

El representante {{representative_name}} es responsable de mantener la confidencialidad de las credenciales de acceso de {{radio_name}}. Cualquier actividad realizada desde su cuenta será considerada como autorizada por usted.

4. PLAN Y FACTURACIÓN

{{radio_name}} utiliza el plan {{plan_name}} con un costo mensual de {{monthly_price}}. La licencia anual {{annual_license_name}} tiene un valor de {{annual_license_amount}}. El storage incluido es de {{storage_limit}}.

5. MODIFICACIONES A LOS TÉRMINOS

RFW se reserva el derecho de modificar estos términos. Los cambios serán notificados con 30 días de anticipación. El uso continuado de la Plataforma constituye aceptación de los nuevos términos.`,
        createdAt: '2026-06-01T14:30:00',
        createdBy: 'Carlos Mendoza',
        notes: 'Versión vigente. Incluye variables de plan, facturación y storage.',
      },
    ],
  },
  {
    id: 2,
    typeKey: 'privacy_policy',
    typeLabel: 'Política de Privacidad',
    typeIcon: 'ri-shield-check-line',
    currentVersion: 2,
    isActive: true,
    createdAt: '2025-01-20T11:00:00',
    updatedAt: '2026-03-10T16:00:00',
    versions: [
      {
        versionNumber: 1,
        title: 'Política de Privacidad — Original',
        status: 'archived',
        content: `POLÍTICA DE PRIVACIDAD

1. INFORMACIÓN QUE RECOLECTAMOS

RFW recolecta información necesaria para la operación de la Plataforma, incluyendo datos de identificación de la radio {{radio_name}}, información de contacto de {{representative_name}} y datos de uso de la Plataforma.

2. USO DE LA INFORMACIÓN

La información recolectada se utiliza exclusivamente para:
- Operar y mejorar la Plataforma
- Comunicar información relevante del servicio
- Cumplir con obligaciones legales

3. PROTECCIÓN DE DATOS

RFW implementa medidas de seguridad técnicas y organizativas para proteger los datos de {{radio_name}} y su representante {{representative_name}}.`,
        createdAt: '2025-01-20T11:00:00',
        createdBy: 'Carlos Mendoza',
        notes: 'Versión inicial.',
      },
      {
        versionNumber: 2,
        title: 'Política de Privacidad — Vigente',
        status: 'active',
        content: `POLÍTICA DE PRIVACIDAD DE RADIOFRECUENCIAWEB

1. RESPONSABLE DEL TRATAMIENTO

{{company_name}}, operador de RadioFrecuenciaWeb, es el responsable del tratamiento de los datos personales de {{radio_name}} y su representante {{representative_name}}.

2. DATOS RECOLECTADOS

Recolectamos los siguientes datos:
- Información de la emisora: nombre, frecuencia {{frequency_web}}, descripción, logo
- Información del representante: nombre {{representative_name}}, email, documento
- Datos de facturación: plan {{plan_name}}, precio {{monthly_price}}
- Datos de uso: estadísticas de audiencia, interacciones en cabina

3. FINALIDAD DEL TRATAMIENTO

Los datos son utilizados para:
- Proveer el servicio contratado bajo el plan {{plan_name}}
- Gestionar la licencia anual {{annual_license_name}}
- Enviar comunicaciones operativas y de facturación
- Mejorar la experiencia de la Plataforma
- Cumplir con obligaciones legales

4. DERECHOS DEL TITULAR

{{representative_name}}, en representación de {{radio_name}}, puede ejercer los derechos de acceso, rectificación, cancelación y oposición contactando a nuestro oficial de privacidad.

5. VIGENCIA

Esta política aplica desde {{contract_date}} y se renueva junto con la licencia anual el {{renewal_date}}.`,
        createdAt: '2026-03-10T16:00:00',
        createdBy: 'María Gutiérrez',
        notes: 'Actualización completa con variables dinámicas y derechos ARCO.',
      },
    ],
  },
  {
    id: 3,
    typeKey: 'radio_contract',
    typeLabel: 'Contrato de Radio',
    typeIcon: 'ri-article-line',
    currentVersion: 2,
    isActive: true,
    createdAt: '2025-02-01T08:00:00',
    updatedAt: '2026-05-15T10:00:00',
    versions: [
      {
        versionNumber: 1,
        title: 'Contrato de Radio — Versión Original',
        status: 'archived',
        content: `CONTRATO DE PRESTACIÓN DE SERVICIOS

Entre {{company_name}}, en adelante "RFW", y {{representative_name}}, en representación de {{radio_name}}, en adelante "la Radio", se celebra el presente contrato:

1. OBJETO

RFW proporciona a {{radio_name}} una plataforma de administración, gestión y promoción para emisoras online en la frecuencia {{frequency_web}} bajo el plan {{plan_name}}.

2. NO PROVEEDOR DE STREAMING

RadioFrecuenciaWeb es una plataforma de administración, gestión, promoción e interacción para emisoras online. No vende streaming de audio, servidores SHOUTcast/Icecast, AutoDJ ni ancho de banda de transmisión. {{radio_name}} usa su propio proveedor de streaming y registra su Stream URL en la Plataforma.

3. VIGENCIA

El presente contrato tiene vigencia desde {{contract_date}} hasta {{renewal_date}}, renovándose automáticamente.`,
        createdAt: '2025-02-01T08:00:00',
        createdBy: 'Carlos Mendoza',
      },
      {
        versionNumber: 2,
        title: 'Contrato de Radio — Vigente',
        status: 'active',
        content: `CONTRATO DE PRESTACIÓN DE SERVICIOS

Entre {{company_name}}, en adelante "RFW", y {{representative_name}}, en representación de {{radio_name}}, en adelante "la Radio", se celebra el presente contrato sujeto a las siguientes cláusulas:

1. OBJETO DEL CONTRATO

RFW proporciona a {{radio_name}} una plataforma de administración, gestión, promoción e interacción para emisoras online en la frecuencia {{frequency_web}} bajo el plan {{plan_name}} con un costo mensual de {{monthly_price}}.

2. NO PROVEEDOR DE STREAMING

RadioFrecuenciaWeb es una plataforma de administración, gestión, promoción e interacción para emisoras online. No vende streaming de audio, servidores SHOUTcast/Icecast, AutoDJ ni ancho de banda de transmisión. La radio {{radio_name}} usa su propio proveedor de streaming y registra su Stream URL en la Plataforma. RFW no interviene en la contratación, calidad ni disponibilidad del servicio de streaming.

3. DERECHOS MUSICALES Y DE AUTOR

Cada radio es responsable de obtener y mantener todos los permisos, licencias, regalías y autorizaciones de derechos de autor correspondientes a todo el contenido que transmita a través de la Plataforma. {{radio_name}} asume la responsabilidad exclusiva por el pago de derechos a entidades de gestión colectiva como SADAIC, ASCAP, BMI, SESAC, AGADU, SAYCO, APDAYC, SPAC y cualquier otra entidad aplicable en su jurisdicción.

4. LICENCIA ANUAL DE FRECUENCIA RFW

La licencia anual {{annual_license_name}} por un valor de {{annual_license_amount}} cubre la reserva exclusiva de la frecuencia {{frequency_web}}, actualizaciones de la Plataforma, mantenimiento del perfil de {{radio_name}}, acceso a la comunidad, presencia en el directorio, soporte básico y mejoras continuas de la Plataforma. Esta licencia es independiente del plan {{plan_name}} y debe renovarse anualmente antes del {{renewal_date}}.

5. STORAGE

{{radio_name}} dispone de {{storage_limit}} de almacenamiento para contenido multimedia. El exceso de uso podrá generar cargos adicionales o la limitación de nuevas cargas.

6. VIGENCIA Y RENOVACIÓN

El presente contrato tiene vigencia desde {{contract_date}} hasta {{renewal_date}}, renovándose automáticamente por períodos iguales salvo comunicación en contrario con 30 días de anticipación.`,
        createdAt: '2026-05-15T10:00:00',
        createdBy: 'Carlos Mendoza',
        notes: 'Versión vigente con cláusulas completas de streaming, música y licencia anual.',
      },
    ],
  },
  {
    id: 4,
    typeKey: 'annual_frequency_license',
    typeLabel: 'Licencia Anual de Frecuencia RFW',
    typeIcon: 'ri-radar-line',
    currentVersion: 2,
    isActive: true,
    createdAt: '2025-03-01T09:00:00',
    updatedAt: '2026-04-20T11:00:00',
    versions: [
      {
        versionNumber: 1,
        title: 'Licencia Anual — Original',
        status: 'archived',
        content: `LICENCIA ANUAL DE FRECUENCIA RFW

1. OBJETO

RFW otorga a {{radio_name}} una licencia anual no exclusiva para el uso de la frecuencia {{frequency_web}} en la Plataforma RadioFrecuenciaWeb.

2. COBERTURA

La licencia {{annual_license_name}} cubre: reserva de frecuencia, presencia en directorio y soporte básico.

3. COSTO

El valor de la licencia es de {{annual_license_amount}} por año, con vencimiento el {{renewal_date}}.`,
        createdAt: '2025-03-01T09:00:00',
        createdBy: 'Carlos Mendoza',
      },
      {
        versionNumber: 2,
        title: 'Licencia Anual — Vigente',
        status: 'active',
        content: `LICENCIA ANUAL DE FRECUENCIA RFW

1. OBJETO Y ALCANCE

RadioFrecuenciaWeb otorga a {{radio_name}}, representada por {{representative_name}}, una licencia anual no exclusiva e intransferible para la utilización de la frecuencia {{frequency_web}} dentro de la Plataforma.

2. COBERTURA DE LA LICENCIA

La {{annual_license_name}}, por un valor de {{annual_license_amount}}, cubre los siguientes servicios:

a) Reserva exclusiva de la frecuencia RFW {{frequency_web}} en el directorio de RFW
b) Actualizaciones de la Plataforma y nuevas funcionalidades
c) Mantenimiento del perfil público de {{radio_name}}
d) Acceso a la comunidad de oyentes y herramientas de interacción
e) Presencia en el directorio y motor de búsqueda de la Plataforma
f) Soporte básico técnico por tickets y chat
g) Mejoras continuas de la Plataforma sin costo adicional

3. EXCLUSIONES

La licencia anual NO incluye:
- Servicio de streaming de audio
- Servidores SHOUTcast o Icecast
- Ancho de banda de transmisión
- AutoDJ
- Software de automatización radial
- Licencias musicales ni derechos de autor

4. VIGENCIA Y RENOVACIÓN

La licencia rige desde {{contract_date}} hasta {{renewal_date}}. La falta de pago de {{annual_license_amount}} dentro de los 30 días posteriores al vencimiento resultará en la suspensión automática de {{radio_name}}.

5. RELACIÓN CON EL PLAN

Esta licencia es independiente del plan {{plan_name}} ({{monthly_price}}/mes) y su storage de {{storage_limit}}. Ambos deben mantenerse al día para la operación continua de {{radio_name}}.`,
        createdAt: '2026-04-20T11:00:00',
        createdBy: 'María Gutiérrez',
        notes: 'Versión completa con todas las coberturas, exclusiones y relación con el plan.',
      },
    ],
  },
  {
    id: 5,
    typeKey: 'content_policies',
    typeLabel: 'Políticas de Contenido',
    typeIcon: 'ri-file-warning-line',
    currentVersion: 2,
    isActive: true,
    createdAt: '2025-02-15T10:00:00',
    updatedAt: '2026-03-25T14:00:00',
    versions: [
      {
        versionNumber: 1,
        title: 'Políticas de Contenido — Original',
        status: 'archived',
        content: `POLÍTICAS DE CONTENIDO

1. RESPONSABILIDAD

{{radio_name}} es la única responsable del contenido que transmite a través de la Plataforma, incluyendo audio, texto, imágenes y videos.

2. CONTENIDO PROHIBIDO

Queda prohibido transmitir contenido que incite a la violencia, discriminación o cualquier actividad ilegal.`,
        createdAt: '2025-02-15T10:00:00',
        createdBy: 'Carlos Mendoza',
      },
      {
        versionNumber: 2,
        title: 'Políticas de Contenido — Vigente',
        status: 'active',
        content: `POLÍTICAS DE CONTENIDO DE RADIOFRECUENCIAWEB

1. RESPONSABILIDAD SOBRE EL CONTENIDO

{{radio_name}}, bajo la representación de {{representative_name}}, es la única y exclusiva responsable de todo el contenido que transmite, publica, comparte o distribuye a través de la Plataforma RFW. Esto incluye, sin limitación:

- Transmisiones de audio en vivo y grabadas
- Textos, descripciones y publicaciones
- Imágenes, fotografías y gráficos
- Videos y transmisiones de video
- Comentarios y mensajes en la cabina
- Cualquier otro material cargado en la Plataforma

2. CONTENIDO PROHIBIDO

Queda terminantemente prohibido transmitir, publicar o distribuir:

a) Contenido que incite a la violencia, el odio o la discriminación
b) Material explícitamente sexual o pornográfico
c) Contenido que promueva actividades ilegales
d) Información falsa o engañosa que pueda causar daño
e) Contenido que infrinja derechos de propiedad intelectual de terceros
f) Spam, phishing o cualquier forma de comunicación no solicitada
g) Contenido que comprometa la seguridad de menores de edad

3. SOBRE EL SERVICIO DE STREAMING

RadioFrecuenciaWeb NO vende streaming de radio. RFW es una plataforma de administración de radio online que ofrece: administración de radio online, perfil público, frecuencia RFW, cabina, chat, comunidad, eventos, fotos, videos, lives dentro de la plataforma, retransmisión futura a redes sociales, directorio y app futura. RFW NO ofrece: servidor de streaming de audio, SHOUTcast, Icecast, AutoDJ, ancho de banda de señal de radio ni licencias musicales. Cada radio es responsable de contratar y mantener su propio proveedor de streaming. La radio proporciona su Stream URL para que RFW pueda reproducirla dentro de la plataforma. Si la URL falla, se cae, no transmite o el proveedor externo tiene problemas, RFW no es responsable por esa interrupción.

4. DERECHOS MUSICALES Y DE AUTOR

Cada radio es responsable de obtener y mantener todos los permisos, licencias, regalías y autorizaciones de derechos de autor correspondientes a la música, podcasts, programas, retransmisiones y contenido protegido que transmita. {{radio_name}} asume la responsabilidad exclusiva por el pago de derechos a entidades de gestión colectiva como SADAIC, ASCAP, BMI, SESAC, AGADU, SAYCO, APDAYC, SPAC y cualquier otra entidad aplicable en su jurisdicción. RFW puede suspender o cancelar cuentas por reclamos válidos, abuso o incumplimiento de estas obligaciones.

5. HORARIO FAMILIAR

{{radio_name}} se compromete a mantener contenido apto para todo público durante el horario de 06:00 a 22:00 (hora local de la emisora), en cumplimiento de las normativas de protección al menor.

6. MODERACIÓN

RFW se reserva el derecho de moderar, suspender o eliminar contenido que viole estas políticas, sin previo aviso y bajo su exclusivo criterio.

7. CONSECUENCIAS

La violación de estas políticas por parte de {{radio_name}} podrá resultar en:
- Advertencia formal
- Suspensión temporal de la cuenta (plan {{plan_name}})
- Cancelación definitiva del servicio
- Denuncia ante las autoridades competentes`,
        createdAt: '2026-03-25T14:00:00',
        createdBy: 'Andrés López',
        notes: 'Ampliada con horario familiar, tipos de contenido, cláusula de streaming, derechos musicales y consecuencias.',
      },
    ],
  },
  {
    id: 6,
    typeKey: 'copyright_music',
    typeLabel: 'Derechos de Autor y Música',
    typeIcon: 'ri-music-2-line',
    currentVersion: 1,
    isActive: true,
    createdAt: '2025-04-10T12:00:00',
    updatedAt: '2025-04-10T12:00:00',
    versions: [
      {
        versionNumber: 1,
        title: 'Derechos de Autor y Música — Vigente',
        status: 'active',
        content: `DERECHOS DE AUTOR Y LICENCIAS MUSICALES

1. PRINCIPIO GENERAL

Cada radio es responsable de obtener y mantener todos los permisos, licencias, regalías y autorizaciones de derechos de autor correspondientes a todo el contenido musical y artístico que transmita a través de la Plataforma RadioFrecuenciaWeb.

2. RESPONSABILIDAD EXCLUSIVA DE LA RADIO

{{radio_name}}, representada por {{representative_name}}, es la única responsable de:

a) Obtener las licencias necesarias de las entidades de gestión colectiva correspondientes, incluyendo pero no limitado a: SADAIC, ASCAP, BMI, SESAC, AGADU, SAYCO, APDAYC, SPAC, SACM, SCD, y cualquier otra entidad aplicable en su jurisdicción.

b) Pagar todas las regalías, derechos de ejecución pública, derechos mecánicos, derechos de sincronización y cualquier otra compensación exigida por la legislación aplicable.

c) Mantener registros actualizados de todas las obras musicales transmitidas.

d) Responder por cualquier reclamo, demanda o acción legal iniciada por titulares de derechos de autor, artistas, sellos discográficos, editoriales musicales o entidades de gestión colectiva.

3. INDEMNIDAD

{{radio_name}} se compromete a mantener indemne a RFW y a {{company_name}} frente a cualquier reclamo de terceros relacionado con la transmisión de contenido protegido por derechos de autor.

4. MÚSICA LIBRE DE DERECHOS

RFW recomienda a {{radio_name}} utilizar música con licencias Creative Commons, música de dominio público o música original, y mantener documentación que acredite la autorización correspondiente.

5. RETRANSMISIÓN

La presente política aplica también a cualquier retransmisión del contenido de {{radio_name}} a través de redes sociales, aplicaciones móviles o cualquier otra plataforma externa.`,
        createdAt: '2025-04-10T12:00:00',
        createdBy: 'Carlos Mendoza',
        notes: 'Documento completo sobre responsabilidad de derechos musicales.',
      },
    ],
  },
  {
    id: 7,
    typeKey: 'suspension_policy',
    typeLabel: 'Política de Suspensión',
    typeIcon: 'ri-pause-circle-line',
    currentVersion: 2,
    isActive: true,
    createdAt: '2025-03-20T09:00:00',
    updatedAt: '2026-02-28T15:00:00',
    versions: [
      {
        versionNumber: 1,
        title: 'Política de Suspensión — Original',
        status: 'archived',
        content: `POLÍTICA DE SUSPENSIÓN

1. CAUSALES DE SUSPENSIÓN

{{radio_name}} podrá ser suspendida por falta de pago de {{monthly_price}} o incumplimiento de los términos.`,
        createdAt: '2025-03-20T09:00:00',
        createdBy: 'Carlos Mendoza',
      },
      {
        versionNumber: 2,
        title: 'Política de Suspensión — Vigente',
        status: 'active',
        content: `POLÍTICA DE SUSPENSIÓN Y REACTIVACIÓN

1. CAUSALES DE SUSPENSIÓN

{{radio_name}} podrá ser suspendida de la Plataforma RFW por las siguientes causas:

a) Falta de pago de la cuota mensual del plan {{plan_name}} ({{monthly_price}}) por más de 15 días corridos
b) Falta de pago de la {{annual_license_name}} ({{annual_license_amount}}) luego del período de gracia de 30 días posteriores al {{renewal_date}}
c) Incumplimiento comprobado de las Políticas de Contenido
d) Violación de derechos de autor según la Política de Derechos de Autor y Música
e) Inactividad de la emisora por más de 60 días corridos sin transmisión
f) Orden judicial o requerimiento de autoridad competente
g) Uso fraudulento de la Plataforma o violación de sistemas de seguridad

2. PROCEDIMIENTO DE SUSPENSIÓN

RFW notificará a {{representative_name}} sobre la causal de suspensión con al menos 7 días de anticipación, excepto en casos de violación grave de contenido, donde la suspensión podrá ser inmediata.

3. EFECTOS DE LA SUSPENSIÓN

Durante la suspensión:
- El perfil de {{radio_name}} no será visible en el directorio público
- La frecuencia {{frequency_web}} quedará reservada por 30 días adicionales
- No se podrá transmitir ni interactuar con la audiencia
- El contenido almacenado se conservará durante 90 días
- El storage {{storage_limit}} no será liberado

4. REACTIVACIÓN

Para reactivar {{radio_name}}, {{representative_name}} deberá:
a) Subsanar la causal de suspensión
b) Abonar los montos adeudados si corresponde
c) Solicitar formalmente la reactivación
d) Firmar nuevamente el contrato si la suspensión superó los 90 días`,
        createdAt: '2026-02-28T15:00:00',
        createdBy: 'Carlos Mendoza',
        notes: 'Política completa con causales, procedimiento y reactivación.',
      },
    ],
  },
  {
    id: 8,
    typeKey: 'storage_policy',
    typeLabel: 'Política de Storage',
    typeIcon: 'ri-hard-drive-2-line',
    currentVersion: 1,
    isActive: true,
    createdAt: '2025-05-01T10:00:00',
    updatedAt: '2025-05-01T10:00:00',
    versions: [
      {
        versionNumber: 1,
        title: 'Política de Storage — Vigente',
        status: 'active',
        content: `POLÍTICA DE ALMACENAMIENTO (STORAGE)

1. ASIGNACIÓN DE STORAGE

{{radio_name}}, bajo el plan {{plan_name}}, dispone de un límite de almacenamiento de {{storage_limit}} para alojar contenido multimedia en la Plataforma RFW, incluyendo:

- Fotografías y galerías de imágenes
- Videos
- Archivos de audio bajo demanda
- Documentos y recursos descargables
- Elementos gráficos del perfil

2. USO DEL STORAGE

{{representative_name}}, como administrador de {{radio_name}}, es responsable de gestionar el uso del storage. La Plataforma mostrará en todo momento el porcentaje ocupado respecto al límite de {{storage_limit}}.

3. EXCESO DE STORAGE

Si {{radio_name}} alcanza el 90% de {{storage_limit}}, recibirá notificaciones automáticas. Al superar el límite, no podrá cargar nuevo contenido hasta:
a) Eliminar contenido existente para liberar espacio
b) Actualizar a un plan superior que ofrezca mayor storage

4. CONTENIDO NO UTILIZADO

RFW se reserva el derecho de notificar sobre contenido que lleve más de 12 meses sin visualizaciones, recomendando su revisión o eliminación.

5. RESPALDO

RFW mantiene copias de seguridad del contenido, pero {{radio_name}} es responsable de mantener sus propias copias de respaldo del material original.`,
        createdAt: '2025-05-01T10:00:00',
        createdBy: 'Diana Ríos',
        notes: 'Política de storage con límites y gestión.',
      },
    ],
  },
  {
    id: 9,
    typeKey: 'app_policy',
    typeLabel: 'Política de Apps',
    typeIcon: 'ri-smartphone-line',
    currentVersion: 1,
    isActive: true,
    createdAt: '2025-06-01T11:00:00',
    updatedAt: '2025-06-01T11:00:00',
    versions: [
      {
        versionNumber: 1,
        title: 'Política de Apps — Vigente',
        status: 'active',
        content: `POLÍTICA DE APLICACIONES MÓVILES

1. APLICACIONES OFICIALES

RadioFrecuenciaWeb ofrece aplicaciones móviles oficiales para iOS y Android que permiten a los oyentes descubrir y escuchar emisoras como {{radio_name}}.

2. PRESENCIA EN LAS APPS

{{radio_name}} aparecerá en las aplicaciones móviles de RFW siempre que:
- Mantenga activo su plan {{plan_name}}
- Su stream esté operativo y accesible
- Cumpla con las Políticas de Contenido
- Tenga al día la {{annual_license_name}}

3. FUNCIONALIDADES DISPONIBLES

A través de las apps, los oyentes de {{radio_name}} en la frecuencia {{frequency_web}} pueden:
- Escuchar la transmisión en vivo
- Ver la programación
- Interactuar en la cabina
- Recibir notificaciones de eventos y transmisiones especiales
- Explorar contenido multimedia (sujeto al storage {{storage_limit}})

4. RESTRICCIONES

RFW no garantiza disponibilidad ininterrumpida de las aplicaciones. Las apps pueden requerir actualizaciones periódicas y conexión a internet. {{radio_name}} no puede exigir funcionalidades personalizadas fuera de las ofrecidas por el plan {{plan_name}}.`,
        createdAt: '2025-06-01T11:00:00',
        createdBy: 'Luis Hernández',
        notes: 'Política de presencia y funcionalidades en apps móviles.',
      },
    ],
  },
  {
    id: 10,
    typeKey: 'rebroadcast_policy',
    typeLabel: 'Política de Retransmisión a Redes',
    typeIcon: 'ri-share-forward-line',
    currentVersion: 1,
    isActive: true,
    createdAt: '2025-06-15T10:00:00',
    updatedAt: '2025-06-15T10:00:00',
    versions: [
      {
        versionNumber: 1,
        title: 'Política de Retransmisión a Redes — Vigente',
        status: 'active',
        content: `POLÍTICA DE RETRANSMISIÓN A REDES SOCIALES Y PLATAFORMAS EXTERNAS

1. ALCANCE

Esta política regula la retransmisión del contenido de {{radio_name}} (frecuencia {{frequency_web}}) hacia plataformas de redes sociales y servicios externos, incluyendo pero no limitado a: Facebook Live, YouTube Live, Twitch, Instagram Live, TikTok Live, X (Twitter) Spaces, y cualquier otra plataforma de difusión.

2. RESPONSABILIDAD

{{radio_name}} y su representante {{representative_name}} son los únicos responsables de:

a) Configurar y gestionar las conexiones de retransmisión
b) Cumplir con los términos de servicio de cada plataforma externa
c) Gestionar los derechos de autor del contenido retransmitido
d) Responder por infracciones de propiedad intelectual en plataformas externas
e) Mantener la calidad técnica de la retransmisión

3. CONTENIDO RETRANSMITIDO

Todo el contenido retransmitido debe cumplir con:
- Las Políticas de Contenido de RFW
- La Política de Derechos de Autor y Música
- Los términos de servicio de cada plataforma de destino
- Las leyes aplicables en cada jurisdicción donde se reciba la señal

4. MONETIZACIÓN

Si {{radio_name}} monetiza su contenido en plataformas externas (ej. Super Chat de YouTube, estrellas de Facebook), RFW no reclama ningún porcentaje de dichos ingresos.

5. STREAM CAÍDO

La retransmisión a redes no sustituye la obligación de {{radio_name}} de mantener operativo su stream principal. La caída del stream en RFW afecta la presencia en el directorio independientemente de si la retransmisión a redes continúa activa.

6. EXENCIÓN DE RESPONSABILIDAD

RFW no es responsable por: bloqueos, strikes, suspensiones o cancelaciones de cuentas que {{radio_name}} reciba en plataformas externas como resultado de sus retransmisiones.`,
        createdAt: '2025-06-15T10:00:00',
        createdBy: 'Sofía Vargas',
        notes: 'Política completa de retransmisión a redes sociales.',
      },
    ],
  },
];

// Helper functions
export function getActiveVersion(template: LegalTemplate): LegalTemplateVersion {
  return template.versions.find((v) => v.status === 'active') || template.versions[template.versions.length - 1];
}

export function getVersionColor(status: LegalTemplateVersion['status']): string {
  switch (status) {
    case 'active': return 'bg-emerald-50 text-emerald-600 border-emerald-200';
    case 'draft': return 'bg-amber-50 text-amber-600 border-amber-200';
    case 'archived': return 'bg-stone-50 text-stone-500 border-stone-200';
    default: return 'bg-background-100 text-foreground-500';
  }
}

export function getVersionLabel(status: LegalTemplateVersion['status']): string {
  switch (status) {
    case 'active': return 'Activa';
    case 'draft': return 'Borrador';
    case 'archived': return 'Archivada';
    default: return status;
  }
}

// Mock data for preview — what the variables render to
export const MOCK_PREVIEW_DATA: Record<string, string> = {
  '{{radio_name}}': 'Radio Estelar 104.5',
  '{{frequency_web}}': '104.5 RFW',
  '{{plan_name}}': 'Pro',
  '{{monthly_price}}': '$19.99 USD',
  '{{annual_license_name}}': 'Licencia Anual de Frecuencia RFW',
  '{{annual_license_amount}}': '$49.95 USD',
  '{{storage_limit}}': '50 GB',
  '{{contract_date}}': '01 de Julio de 2026',
  '{{renewal_date}}': '01 de Julio de 2027',
  '{{representative_name}}': 'Carlos Mendoza',
  '{{company_name}}': 'Estelar Media Group S.A.',
};